/*
These resources are a temporary solution to help with the server side rendering of multi help center home page.
This module will not be needed, once we move to the Flexible home page built on top of the Layout system.
*/
import { fetchHelpCenterBrandingAction } from 'epics/model/help-center';
import { store } from 'providers';
import { createResource } from 'react-resource-router';
import { getRequest } from 'relay-runtime';
import { fetchFeaturedAndSortedPortalsModel } from 'state/actions/featured-and-sorted-portals';
import {
	updateHCBrandingLoaderAction,
	updateHCBrandingModelAction,
} from 'state/actions/help-center';
import type { topicsQuery } from 'view/help-center-topics/topics/__generated__/topicsQuery.graphql';
import TopicsQuery from 'view/help-center-topics/topics/__generated__/topicsQuery.graphql';
import FeaturedAndSortedPortalsQuery from '@atlassian/help-center-common-util/graphql-relay-connector/featured-and-sorted-portals/__generated__/featuredAndSortedPortalsQuery.graphql';
import BrandingQuery from '@atlassian/help-center-common-util/graphql-relay-connector/help-center-branding/__generated__/helpCenterBrandingQuery.graphql';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	isCSMHelpCenter,
	isDefaultHelpCenter,
} from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';
import {
	convertToHCBrandingResponse,
	fetchBrandingData,
} from '@atlassian/help-center-common-util/graphql-relay-connector';
import { aggPromiseCache } from '@atlassian/help-center-common-util/relay/fetch-v2';
import { createRelayResource } from '@atlassian/help-center-common-util/resource-router';

const JSM_SSR_NON_DEFAULT_HC_BRANDING = 'jsm_help_center_ssr_non_default_hc_branding';
const isJsmSSRNonDefaultHCBrandingEnabled = (): boolean =>
	FeatureGates.checkGate(JSM_SSR_NON_DEFAULT_HC_BRANDING);

const getBrandingKey = (
	_: unknown,
	{ cloudId, helpCenterAri }: { cloudId: string; helpCenterAri: string },
) => {
	const request = getRequest(BrandingQuery);
	const params = request.params;
	return `${String(params.id)}${JSON.stringify({
		cloudId,
		helpCenterAri,
	})}`;
};

export const brandingResource = createResource({
	type: 'RELAY_HELP_CENTER_BRANDING',
	getKey: getBrandingKey,
	getData: () => {
		store.dispatch(fetchHelpCenterBrandingAction());
		return Promise.resolve(null);
	},
});

/**
 * Unlike the default help center, a non-default JSM help center doesn't get branding in the initial
 * JSON payload, and the client-side fetch (componentDidMount) never runs during SSR.
 *
 * The branding is written to redux here rather than via the epic: SSR renders once the AGG promises
 * settle, which is before the epic's dispatch lands, so the whole fetch-and-dispatch chain is
 * registered in `aggPromiseCache` for the SSR entry to await.
 */
export const jsmNonDefaultHCBrandingResource = createResource({
	type: 'RELAY_JSM_NON_DEFAULT_HELP_CENTER_BRANDING',
	getKey: getBrandingKey,
	getData: () => {
		if (
			!isJsmSSRNonDefaultHCBrandingEnabled() ||
			isDefaultHelpCenter() ||
			isCSMHelpCenter(getEnv().helpCenterType)
		) {
			return Promise.resolve(null);
		}

		const brandingPromise = fetchBrandingData()
			.toPromise()
			.then((response) => {
				const branding = convertToHCBrandingResponse(response);
				if (branding) {
					store.dispatch(updateHCBrandingModelAction(branding));
				}
				store.dispatch(updateHCBrandingLoaderAction(true));
				return null;
			})
			.catch(() => {
				store.dispatch(updateHCBrandingLoaderAction(true));
				return null;
			});

		if (__SERVER__) {
			aggPromiseCache.add(brandingPromise);
		}
		return brandingPromise;
	},
});

export const featuredAndSortedPortalsResource = createResource({
	type: 'RELAY_HELP_CENTER_PORTALS',
	getKey: (_, { cloudId, helpCenterAri }: { cloudId: string; helpCenterAri: string }) => {
		const request = getRequest(FeaturedAndSortedPortalsQuery);
		const params = request.params;
		return `${String(params.id)}${JSON.stringify({
			cloudId,
			helpCenterAri,
		})}`;
	},
	getData: () => {
		store.dispatch(fetchFeaturedAndSortedPortalsModel({}, false));
		return Promise.resolve(null);
	},
});

export const topicsResource = createRelayResource<topicsQuery>({
	type: 'HELP_CENTER_TOPICS',
	getQuery: (_, { cloudId, helpCenterAri }: { cloudId: string; helpCenterAri: string }) => {
		return {
			parameters: TopicsQuery,
			variables: {
				cloudId,
				helpCenterAri,
			},
			options: {
				fetchPolicy: 'store-or-network',
			},
		};
	},
});
